import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiFileCopyLine } from '@remixicon/react'
import {
  PUBLIC_LINK_LIMITS,
  type PublicLinkState,
  type SurveyDetail,
} from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  Field,
  Input,
  SectionSpinner,
  Textarea,
} from '../ui'
import AnswerList from './AnswerList'
import { api, ApiError } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDateTime } from '../../lib/dates'

interface PublicLinkPanelProps {
  workspaceId: string
  survey: SurveyDetail
  /** Switching the link on or off is an admin's act. Operators only read what arrived. */
  isAdmin: boolean
  locale: string
}

/**
 * The public link of a survey, and what came in through it.
 *
 * The link itself is shown once, when it is switched on, and never again: only
 * its hash is stored. Losing it means replacing it, which kills the old one.
 */
export default function PublicLinkPanel({
  workspaceId,
  survey,
  isAdmin,
  locale,
}: PublicLinkPanelProps) {
  const { t } = useTranslation()
  const base = `/api/workspaces/${workspaceId}/surveys/${survey.id}`

  const [state, setState] = useState<PublicLinkState | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [freshUrl, setFreshUrl] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const [expiresAt, setExpiresAt] = useState('')
  const [maxResponses, setMaxResponses] = useState(String(PUBLIC_LINK_LIMITS.defaultMaxResponses))
  const [notice, setNotice] = useState('')
  // `null`: the admin has not chosen, and the choice follows what is possible. The
  // proof is the default whenever the keys exist; without them it cannot be asked.
  const [antibotChoice, setAntibotChoice] = useState<boolean | null>(null)
  // Without the proof, the admin says they know what is left to hold the link.
  const [ackOff, setAckOff] = useState(false)

  const load = useCallback(async () => {
    const response = await api.get<{ data: PublicLinkState }>(`${base}/public`)
    setState(response.data)
  }, [base])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err)))
      .finally(() => setLoading(false))
  }, [load])

  const activate = async () => {
    setSaving(true)
    setError(null)

    try {
      const response = await api.post<{ public_url: string }>(`${base}/public-link`, {
        expires_at: expiresAt,
        max_responses: Number(maxResponses),
        privacy_notice: notice,
        antibot: useAntibot,
      })
      setFreshUrl(response.public_url)
      setCopied(false)
      // The next link starts from the default again, as the server does: a choice
      // made for this one must not travel to the one that replaces it.
      setAntibotChoice(null)
      setAckOff(false)
      await load()
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'antibot_not_configured'
          ? t('publicLink.antibotMissing')
          : (toErrorMessage(err) ?? t('errors.generic')),
      )
    } finally {
      setSaving(false)
    }
  }

  const deactivate = async () => {
    setError(null)
    try {
      await api.delete(`${base}/public-link`)
      setFreshUrl(null)
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const copy = async () => {
    if (!freshUrl) return
    try {
      await navigator.clipboard.writeText(freshUrl)
      setCopied(true)
    } catch {
      /* the field is selectable: copying by hand still works */
    }
  }

  if (loading) return <SectionSpinner />
  if (!state) return <EmptyState title={t('errors.notFound')} description={error ?? undefined} />

  const canSwitchOn = isAdmin
  const useAntibot = state.antibot_configured && (antibotChoice ?? true)

  return (
    <div className="space-y-6">
      {error && <Alert>{error}</Alert>}

      {!state.antibot_configured && (
        <Alert variant="warning">{t('publicLink.antibotMissing')}</Alert>
      )}

      {state.active && !state.antibot && (
        <Alert variant="warning">{t('publicLink.activeWithoutAntibot')}</Alert>
      )}

      <Card className="space-y-4 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
            {t('publicLink.title')}
          </h3>
          <Badge variant={state.active ? 'success' : 'neutral'}>
            {t(state.active ? 'publicLink.active' : 'publicLink.inactive')}
          </Badge>
        </div>

        {state.expires_at && (
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('publicLink.summary', {
              given: state.count,
              max: state.max_responses ?? 0,
              date: formatDateTime(`${state.expires_at.replace(' ', 'T')}Z`, locale),
            })}
          </p>
        )}

        {freshUrl && (
          <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-900 dark:bg-emerald-950/30">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {t('publicLink.freshTitle')}
            </p>
            <div className="flex gap-2">
              <Input readOnly value={freshUrl} onFocus={(event) => event.target.select()} />
              <Button variant="secondary" onClick={copy}>
                <RiFileCopyLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
                {copied ? t('publicLink.copied') : t('publicLink.copy')}
              </Button>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-300">{t('publicLink.freshNote')}</p>
          </div>
        )}

        {isAdmin ? (
          <div className="space-y-4 border-t border-gray-100 pt-4 dark:border-gray-800">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-50">
              {t(state.active ? 'publicLink.replaceTitle' : 'publicLink.switchOnTitle')}
            </h4>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('publicLink.expiresAt')} hint={t('publicLink.expiresAtHint')} required>
                <Input
                  type="date"
                  value={expiresAt}
                  onChange={(event) => setExpiresAt(event.target.value)}
                />
              </Field>
              <Field
                label={t('publicLink.maxResponses')}
                hint={t('publicLink.maxResponsesHint', {
                  fallback: PUBLIC_LINK_LIMITS.defaultMaxResponses,
                })}
                required
              >
                <Input
                  type="number"
                  min={1}
                  max={PUBLIC_LINK_LIMITS.maxMaxResponses}
                  value={maxResponses}
                  onChange={(event) => setMaxResponses(event.target.value)}
                />
              </Field>
            </div>

            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={useAntibot}
                disabled={!state.antibot_configured}
                onChange={(event) => setAntibotChoice(event.target.checked)}
                className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span>
                <span className="block font-medium text-gray-900 dark:text-gray-50">
                  {t('publicLink.antibotLabel')}
                </span>
                <span className="mt-0.5 block text-gray-500 dark:text-gray-400">
                  {t(
                    state.antibot_configured
                      ? 'publicLink.antibotHint'
                      : 'publicLink.antibotUnavailableHint',
                  )}
                </span>
              </span>
            </label>

            {!useAntibot && (
              <>
                <Alert variant="warning">{t('publicLink.antibotOffWarning')}</Alert>
                <label className="flex items-start gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={ackOff}
                    onChange={(event) => setAckOff(event.target.checked)}
                    className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="font-medium text-gray-900 dark:text-gray-50">
                    {t('publicLink.antibotOffAck')}
                  </span>
                </label>
              </>
            )}

            <Field label={t('publicLink.privacyNotice')} hint={t('publicLink.privacyNoticeHint')} required>
              <Textarea
                rows={5}
                maxLength={PUBLIC_LINK_LIMITS.maxPrivacyNoticeLength}
                value={notice}
                onChange={(event) => setNotice(event.target.value)}
              />
            </Field>

            <div className="flex flex-wrap justify-end gap-3">
              {state.active && (
                <ConfirmDialog
                  title={t('publicLink.deactivateTitle')}
                  description={t('publicLink.deactivateConfirm')}
                  confirmLabel={t('publicLink.deactivate')}
                  onConfirm={deactivate}
                  trigger={<Button variant="secondary">{t('publicLink.deactivate')}</Button>}
                />
              )}
              {state.active ? (
                // Replacing ends the link that was handed out: ask first.
                <ConfirmDialog
                  title={t('publicLink.replaceConfirmTitle')}
                  description={t('publicLink.replaceConfirm')}
                  confirmLabel={t('publicLink.replace')}
                  onConfirm={activate}
                  trigger={
                    <Button
                      loading={saving}
                      disabled={!canSwitchOn || !expiresAt || !notice.trim() || (!useAntibot && !ackOff)}
                    >
                      {t('publicLink.replace')}
                    </Button>
                  }
                />
              ) : (
                <Button
                  onClick={activate}
                  loading={saving}
                  disabled={!canSwitchOn || !expiresAt || !notice.trim() || (!useAntibot && !ackOff)}
                >
                  {t('publicLink.switchOn')}
                </Button>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">{t('publicLink.adminOnly')}</p>
        )}
      </Card>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
          {t('publicLink.responsesTitle', { total: state.count })}
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t(survey.anonymous ? 'publicLink.responsesNoteAnonymous' : 'publicLink.responsesNote')}
        </p>

        {state.responses_withheld && (
          <Alert variant="info">
            {t('publicLink.withheld', { given: state.count, threshold: state.threshold })}
          </Alert>
        )}

        {state.truncated && (
          <Alert variant="info">
            {t('publicLink.truncated', { shown: state.responses.length, total: state.count })}
          </Alert>
        )}

        {state.responses.length === 0 && !state.responses_withheld ? (
          <Card>
            <EmptyState
              title={t('publicLink.noResponsesTitle')}
              description={t('publicLink.noResponsesDescription')}
            />
          </Card>
        ) : (
          state.responses.map((response, index) => (
            <Card key={index} className="divide-y divide-gray-100 p-0 dark:divide-gray-800">
              <p className="p-4 text-sm font-medium text-gray-900 dark:text-gray-50">
                {response.respondent_name
                  ? t('publicLink.declaredName', { name: response.respondent_name })
                  : t('publicLink.anonymousResponse', { n: index + 1 })}
              </p>
              <AnswerList
                survey={survey}
                answers={response.answers.map((answer) => ({
                  ...answer,
                  answered_by: null,
                  answered_at: '',
                }))}
                locale={locale}
              />
            </Card>
          ))
        )}
      </div>
    </div>
  )
}
